// TEAM-KPI (founder-approved 2026-10-21; spec 13 §Team KPIs and per-metric warning bands): the
// KPI dialog's "Warning bands" step. A metric reads green, amber or red by its attainment of the
// target: the company's fixed bands (green ≥ 100%, amber ≥ 70%) unless the metric has its own.
// The live preview draws the 0–150% scale in the three band colours with the thresholds, and —
// once a target is entered — what three sample values would read (the value, its dot and its
// label), so a manager sees "6 days to shortlist is amber" before saving. The rules are the
// domain's (`metricHealth`, `bandsProblem`); bands belong to the metric, not the seat or team.
import { bandsProblem, MAX_BAND } from "@companyos/domain-kpi";
import { Field, FieldLabel, Input, LensBandDot } from "@companyos/ui";
import type { MetricDef } from "../../api/types";
import { formatMetricValue } from "../format";
import {
  DEFAULT_METRIC_BANDS,
  HEALTH_LABEL,
  healthTone,
  type MetricBands,
  metricHealth,
} from "./health";

/** The form's bands: the company's, or the metric's own as typed. */
export type BandsForm = { custom: boolean; green: string; amber: string };

export const bandsFormOf = (b: MetricBands | null | undefined): BandsForm =>
  b
    ? { custom: true, green: String(b.green), amber: String(b.amber) }
    : {
        custom: false,
        green: String(DEFAULT_METRIC_BANDS.green),
        amber: String(DEFAULT_METRIC_BANDS.amber),
      };

/** The form as stored bands (null: the company's), or the reason it cannot be stored. */
export function bandsOfForm(f: BandsForm): { bands: MetricBands | null } | { error: string } {
  if (!f.custom) return { bands: null };
  if (!f.green.trim() || !f.amber.trim()) return { error: "Both band thresholds are numbers." };
  const bands = { green: Number(f.green), amber: Number(f.amber) };
  const why = bandsProblem(bands);
  return why ? { error: `Warning bands: ${why}.` } : { bands };
}

/** Whether two band settings differ (null = the company's). */
export const bandsChanged = (a: MetricBands | null | undefined, b: MetricBands | null) =>
  (a ?? null) === null ? b !== null : b === null || a?.green !== b.green || a?.amber !== b.amber;

/**
 * The value that reaches `pct`% of `target` for a direction (the inverse of domain-kpi
 * `attainment`): `gte`/`gt` scale up, `lte`/`lt` scale down; `eq` and `between` have no single
 * value per percentage (null).
 */
export function valueAt(
  direction: MetricDef["direction"],
  target: number,
  pct: number,
): number | null {
  if (pct <= 0) return null;
  if (direction === "gte" || direction === "gt") return (target * pct) / 100;
  if (direction === "lte" || direction === "lt") return (target * 100) / pct;
  return null;
}

/** The preview's sample readings: just red, the amber start and the green start. */
export function samplePercents(b: MetricBands): number[] {
  const red = Math.max(1, b.amber - 15);
  return [...new Set([red, b.amber, b.green].map((x) => Math.round(x * 100) / 100))].filter(
    (x) => x > 0,
  );
}

export function BandsEditor({
  value,
  onChange,
  direction,
  target,
  unit,
  currency,
  metricName,
}: {
  value: BandsForm;
  onChange: (next: BandsForm) => void;
  direction: MetricDef["direction"];
  /** The target as stored (null: none entered yet; the preview shows the scale only). */
  target: number | null;
  unit: MetricDef["unit"];
  currency: string;
  metricName: string | null;
}) {
  const parsed = bandsOfForm(value);
  const bands = "bands" in parsed ? (parsed.bands ?? DEFAULT_METRIC_BANDS) : null;
  return (
    <div className="flex flex-col gap-3" data-slot="bands-editor">
      <fieldset aria-label="Warning bands" className="m-0 flex flex-col gap-1.5 border-0 p-0">
        {(
          [
            {
              custom: false,
              label: `Company bands (green ≥ ${DEFAULT_METRIC_BANDS.green}%, amber ≥ ${DEFAULT_METRIC_BANDS.amber}%)`,
            },
            { custom: true, label: "Custom bands for this metric" },
          ] as const
        ).map((o) => (
          <label key={String(o.custom)} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="kpi-bands"
              className="accent-primary"
              checked={value.custom === o.custom}
              onChange={() => onChange({ ...value, custom: o.custom })}
            />
            {o.label}
          </label>
        ))}
      </fieldset>
      {value.custom ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field data-invalid={"error" in parsed ? true : undefined}>
            <FieldLabel htmlFor="kpi-band-green">Green at ≥ (% of target)</FieldLabel>
            <Input
              id="kpi-band-green"
              inputMode="decimal"
              value={value.green}
              onChange={(e) => onChange({ ...value, green: e.target.value })}
            />
          </Field>
          <Field data-invalid={"error" in parsed ? true : undefined}>
            <FieldLabel htmlFor="kpi-band-amber">Amber at ≥ (% of target)</FieldLabel>
            <Input
              id="kpi-band-amber"
              inputMode="decimal"
              value={value.amber}
              onChange={(e) => onChange({ ...value, amber: e.target.value })}
            />
          </Field>
        </div>
      ) : null}
      <section
        className="flex flex-col gap-2 rounded-lg border border-border p-3"
        data-slot="bands-preview"
        aria-label="Warning bands preview"
      >
        {bands ? (
          <>
            {/* The 0–150% scale in the band colours (SVG geometry: no inline styles). */}
            <svg
              viewBox={`0 0 ${MAX_BAND} 10`}
              preserveAspectRatio="none"
              className="h-2.5 w-full overflow-hidden rounded-full"
              aria-hidden="true"
            >
              <rect x={0} y={0} width={bands.amber} height={10} className="fill-band-low" />
              <rect
                x={bands.amber}
                y={0}
                width={bands.green - bands.amber}
                height={10}
                className="fill-band-average"
              />
              <rect
                x={bands.green}
                y={0}
                width={MAX_BAND - bands.green}
                height={10}
                className="fill-band-great"
              />
            </svg>
            <p className="flex flex-wrap justify-between gap-x-3 text-2xs text-muted-foreground tabular-nums">
              <span>0%</span>
              <span>Red below {bands.amber}%</span>
              {bands.green !== bands.amber ? <span>Amber from {bands.amber}%</span> : null}
              <span>Green from {bands.green}%</span>
              <span>{MAX_BAND}%</span>
            </p>
            {target !== null ? (
              <ul className="flex flex-col gap-1 text-xs" data-slot="bands-samples">
                {samplePercents(bands).map((pct) => {
                  const v = valueAt(direction, target, pct);
                  const h = metricHealth(pct / 100, bands);
                  return (
                    <li key={pct} className="flex items-center gap-2" data-health={h ?? "none"}>
                      <LensBandDot band={healthTone(h)} />
                      <span className="tabular-nums">
                        {v === null
                          ? `${pct}% of target`
                          : `${formatMetricValue(v, unit, currency)} (${pct}% of target)`}
                      </span>
                      <span className="text-muted-foreground">· {HEALTH_LABEL[h ?? "none"]}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">
                Enter a target to see what values read green, amber or red.
              </p>
            )}
          </>
        ) : (
          <p className="text-xs text-danger-text" role="alert">
            {"error" in parsed ? parsed.error : null}
          </p>
        )}
      </section>
      <p className="text-xs text-muted-foreground" data-slot="bands-note">
        Bands belong to {metricName ?? "the metric"}: they colour it on every seat and team that
        uses it. The seat and team scores keep the company's bands.
      </p>
    </div>
  );
}
